// Regra única de mensagens de WhatsApp por forma de pagamento, usada em todo o app.
//
// - channel "mercadopago" (ex.: pix online, cartão, boleto): a cobrança é feita via
//   link/QR do Mercado Pago (init_point). NÃO enviamos chave Pix manual nesse caso.
// - channel "comprovante" (pix_comprovante): enviamos o Pix copia-e-cola (chave manual)
//   e pedimos explicitamente o envio do comprovante por WhatsApp.
// - demais formas (presencial etc.): mensagem simples, sem dados de pagamento online.

import { supabase } from "@/integrations/supabase/client";
import { buildPixBrCode } from "@/lib/pix/brcode";
import type { PaymentChannel } from "@/lib/payment-methods";

export interface MercadoPagoLinkParams {
  training_center_id: string;
  transaction_amount: number;
  description: string;
  payer_email?: string | null;
  reference_id: string;
  /** Forma de pagamento escolhida (pix | cartao_credito | boleto). Default: pix. */
  method?: string | null;
}

export interface MercadoPagoLinkResult {
  init_point: string | null;
  brcode: string;
  qr_code_base64?: string | null;
}

const isCardMethod = (m?: string | null) => !!m && /cartao|cart\u00e3o/.test(m) && !m.includes("presencial");
const isBoletoMethod = (m?: string | null) => m === "boleto";

/**
 * Cria a cobrança online no Mercado Pago conforme a forma de pagamento:
 * - pix    → Pix dinâmico (copia-e-cola + link)
 * - cartão / boleto → preferência de Checkout (link) restrita ao meio escolhido
 */
export async function createMercadoPagoPixLink(p: MercadoPagoLinkParams): Promise<MercadoPagoLinkResult> {
  if (isCardMethod(p.method) || isBoletoMethod(p.method)) {
    const allowed = isBoletoMethod(p.method) ? ["boleto"] : ["cartao"];
    const { data, error } = await supabase.functions.invoke("create-payment-preference", {
      body: {
        training_center_id: p.training_center_id,
        transaction_amount: p.transaction_amount,
        description: p.description,
        payer_email: p.payer_email || "cliente@email.com",
        reference_id: p.reference_id,
        allowed_methods: allowed,
      },
    });
    if (error) throw error;
    return { init_point: data?.init_point ?? null, brcode: "", qr_code_base64: null };
  }

  const { data, error } = await supabase.functions.invoke("create-pix-payment", {
    body: {
      training_center_id: p.training_center_id,
      transaction_amount: p.transaction_amount,
      description: p.description,
      payer_email: p.payer_email || "cliente@email.com",
      reference_id: p.reference_id,
    },
  });
  if (error) throw error;
  return {
    init_point: data?.init_point ?? null,
    brcode: data?.qr_code ?? "",
    qr_code_base64: data?.qr_code_base64 ?? null,
  };
}

export interface PaymentMessagesParams {
  /** Canal da forma de pagamento (ver src/lib/payment-methods.ts). */
  channel: PaymentChannel | null;
  /** 1ª mensagem, já com todos os dados da cobrança (aluno, valor, vencimento, etc). */
  introMessage: string;
  amount: number;
  /** Dados usados apenas quando channel === "comprovante". */
  pixKey?: string | null;
  pixBeneficiary?: string | null;
  city?: string | null;
  txid?: string;
  description?: string;
  /** Dados usados apenas quando channel === "mercadopago" (ver createMercadoPagoPixLink). */
  mercadoPago?: { initPoint?: string | null; brcode?: string | null };
  /** Forma de pagamento escolhida — usada para rotular a mensagem online. */
  method?: string | null;
  /** Mensagem extra usada para canais que não são online nem comprovante (ex.: presencial). */
  fallbackMessage?: string;
}

/**
 * Monta o array de mensagens (para envio em sequência via sendWhatsappSequence) seguindo
 * a regra padrão de pagamento: Pix Mercado Pago → link; Pix com comprovante → copia-e-cola
 * + aviso de envio do comprovante; demais formas → mensagem única (fallback opcional).
 */
export function buildPaymentWhatsappMessages(p: PaymentMessagesParams): string[] {
  const msgs: string[] = [p.introMessage];

  if (p.channel === "comprovante") {
    const brcode = p.pixKey
      ? buildPixBrCode({
          pixKey: p.pixKey,
          beneficiaryName: p.pixBeneficiary || "PAGADOR",
          city: p.city || "BRASIL",
          amount: p.amount > 0 ? p.amount : undefined,
          txid: p.txid,
          description: p.description,
        })
      : "";
    msgs.push(
      `📎 *Após pagar, ENVIE O COMPROVANTE neste mesmo número.*\n` +
        (p.pixBeneficiary ? `Favorecido: *${p.pixBeneficiary}*\n` : "") +
        (brcode
          ? `Pix *copia-e-cola* (valor já incluso) na próxima mensagem — toque e segure para copiar.`
          : `Chave Pix: ${p.pixKey ?? "(não configurada)"}`),
    );
    if (brcode) msgs.push(brcode);
  } else if (p.channel === "mercadopago") {
    const kind = isBoletoMethod(p.method)
      ? "Boleto"
      : isCardMethod(p.method)
        ? "Cart\u00e3o de Cr\u00e9dito"
        : "Pix";
    msgs.push(
      `💳 *Pagamento via Mercado Pago \u2014 ${kind}*\n` +
        (p.mercadoPago?.initPoint ? `Link: ${p.mercadoPago.initPoint}\n` : "") +
        `Assim que o pagamento for compensado, a baixa é feita automaticamente.`,
    );
    if (p.mercadoPago?.brcode) {
      msgs.push(`📋 Pix *copia-e-cola* (toque e segure para copiar):`);
      msgs.push(p.mercadoPago.brcode);
    }
  } else if (p.fallbackMessage) {
    msgs.push(p.fallbackMessage);
  }

  return msgs;
}
